import { Container, Graphics, type FederatedPointerEvent } from 'pixi.js';
import { getActiveDevice } from '@/input/active-device';
import { sfx } from '@/audio/sfx';
import { composeBay, type BayHooks, type BayParts } from './shop-compose';
import { composeCompact } from './shop-compact';
import {
  COMPACT_H, COMPACT_W, GAIN_ANCHORS_C, compactFocus, compactItemPos, isCompactShop, pageOf,
} from './shop-compact-layout';
import {
  buildFocusGroups, type FocusItem, type ShopCallbacks, type ShopPage, type ShopState,
} from './shop-types';
import { spatialMove } from './shop-nav';
import { ShopGains, type GainKind } from './shop-gains';
import { CHIPS_PER_ROW, CHIP_ROWS, DESIGN_H, DESIGN_W, itemPos } from './shop-layout';
import { HangarInteraction } from './shop-hangar-input';

export type { FocusItem, ShopCallbacks, ShopState } from './shop-types';

const same = (a: FocusItem | undefined, b: FocusItem | undefined): boolean =>
  JSON.stringify(a) === JSON.stringify(b);

/**
 * Break screen — "UPGRADE BAY". docs/03, docs/09. One linear focus list,
 * everything clickable, responsive via design-space scaling. Touch is
 * ONE tap (wave 4): a tap focuses AND acts — hover-focus is mouse-only so
 * no rebuild can eat the tap. Phone-sized canvases get the COMPACT bay
 * (polish 1, shop-compact*.ts): its own design space, three glyph tabs.
 */
export class UpgradeScreen {
  readonly view = new Container();
  private readonly dim = new Graphics();
  private readonly layer = new Container();
  /** Persistent fx overlay (NOT cleared on rebuild) for buy-gain animations */
  private readonly gains = new ShopGains();
  private readonly hangar: HangarInteraction;
  open = false;
  /** Weapon info panel toggled with Y/I (docs/12 M1) */
  private infoOpen = false;

  private items: FocusItem[] = [];
  /** Design-space center of every focusable item — drives spatial nav (#2) */
  private positions: { x: number; y: number }[] = [];
  private selected = 0;
  private state: ShopState | null = null;
  /** Focus to restore after the next refresh (equip moves the hand) */
  private wanted: FocusItem | null = null;
  /** Phone layout + its open tab */
  private compact = false;
  private page: ShopPage = 'cards';

  constructor(private readonly callbacks: ShopCallbacks) {
    this.hangar = new HangarInteraction(callbacks);
    this.view.visible = false;
    this.view.addChild(this.dim, this.layer, this.gains.view, this.hangar.fly.view);
  }

  show(screenW: number, screenH: number, state: ShopState): void {
    this.open = true;
    this.view.visible = true;
    this.items = [];
    this.selected = 0;
    this.state = null; // the resize below must not redraw the last break
    this.hangar.reset();
    // A waiting crate or a better new weapon is the obvious first move,
    // else the first card (compact: not the tab row above it)
    this.wanted = this.hangar.firstFocus(state) ?? (state.offer.length > 0 ? { kind: 'card', index: 0 } : null);
    this.page = (this.wanted && pageOf(this.wanted)) ?? 'cards';
    this.resize(screenW, screenH);
    this.refresh(state);
  }

  /** Re-fit dim + design-space scale (fullscreen toggles, window resizes). */
  resize(screenW: number, screenH: number): void {
    this.dim.clear().rect(0, 0, screenW, screenH).fill({ color: 0x0a0614, alpha: 0.94 });
    const compact = isCompactShop(screenW, screenH);
    const switched = compact !== this.compact;
    this.compact = compact;
    this.hangar.posOf = compact ? compactItemPos : itemPos;
    this.gains.setAnchors(compact ? GAIN_ANCHORS_C : null);
    const dw = compact ? COMPACT_W : DESIGN_W;
    const dh = compact ? COMPACT_H : DESIGN_H;
    const scale = Math.min(screenW / dw, screenH / dh);
    this.layer.scale.set(scale);
    this.layer.position.set((screenW - dw * scale) / 2, (screenH - dh * scale) / 2);
    for (const fx of [this.gains.view, this.hangar.fly.view]) {
      fx.scale.copyFrom(this.layer.scale);
      fx.position.copyFrom(this.layer.position);
    }
    // Rotated / resized across the phone threshold: other layout, other
    // focus list — the compact page follows the focused item
    if (switched && this.open && this.state) {
      const cur = this.items[this.selected];
      if (compact && cur) this.page = pageOf(cur) ?? this.page;
      this.refresh(this.state);
    }
  }

  /** Buy feedback (docs/12 #1) — see shop-gains.ts. */
  flashGain(kind: GainKind, amount: number): void {
    this.gains.flash(kind, amount);
  }

  /** Per-frame tick (driven by the game loop while the bay is open). */
  tick(dt: number): void {
    this.gains.tick(dt);
    this.hangar.fly.tick(dt);
  }

  refresh(state: ShopState): void {
    this.state = state;
    const prev = this.wanted ?? this.items[this.selected];
    this.wanted = null;
    // Only VISIBLE chips are focusable (no ghost indexes); compact: one page
    this.items = this.compact
      ? compactFocus(state, this.page)
      : buildFocusGroups(state, CHIPS_PER_ROW * CHIP_ROWS).flat();
    this.positions = this.items.map((it) => this.hangar.posOf(it, state));
    // Keep focus on the same item across refreshes (buys/trashes reshuffle
    // the flat list); fall back to a clamped index when the item vanished.
    const kept = prev ? this.items.findIndex((it) => same(it, prev)) : -1;
    this.selected = kept >= 0 ? kept : Math.max(0, Math.min(this.selected, this.items.length - 1));
    this.rebuild();
  }

  private hooks(): BayHooks {
    return {
      isSel: (item) => this.isSel(item),
      interactive: (node, item) => this.interactive(node, item),
    };
  }

  private isSel(item: FocusItem): boolean {
    return same(this.items[this.selected], item);
  }

  private interactive(node: Container, item: FocusItem): void {
    node.eventMode = 'static';
    node.cursor = 'pointer';
    // Hover-focus is for the mouse only: on touch the rebuild would destroy
    // the node before its pointertap fires (the old "tap twice" bug)
    node.on('pointerover', (e: FederatedPointerEvent) => {
      if (e.pointerType === 'mouse') this.focusItem(item);
    });
    node.on('pointertap', () => {
      this.select(item);
      this.activate(item);
    });
  }

  private select(item: FocusItem): boolean {
    const idx = this.items.findIndex((it) => same(it, item));
    if (idx < 0 || idx === this.selected) return false;
    this.selected = idx;
    this.hangar.armed = null;
    return true;
  }

  private focusItem(item: FocusItem): void {
    if (this.select(item)) this.rebuild();
  }

  private rebuild(): void {
    const s = this.state;
    if (!s) return;
    this.layer.removeChildren().forEach((c) => c.destroy({ children: true }));
    const sel = this.items[this.selected];
    const parts: BayParts = {
      base: this.hooks(),
      hangar: this.hangar.hooks(s, sel, this.hooks(), {
        scrap: (id) => this.scrapItem(id),
        swap: (id) => {
          this.select({ kind: 'weapon', weaponId: id });
          this.activate({ kind: 'weapon', weaponId: id });
        },
      }),
      prompt: this.hangar.prompt(s, sel),
      infoOpen: this.infoOpen,
      toggleLock: (i) => this.callbacks.toggleLock(i),
      openMenu: getActiveDevice() === 'gamepad' ? null : () => this.callbacks.openMenu(),
    };
    if (this.compact) composeCompact(this.layer, s, sel, this.page, parts);
    else composeBay(this.layer, s, sel, parts);
  }

  /** ←/→ — spatial move to the nearest item horizontally (#2 gamepad flow). */
  moveSelection(dir: number): void {
    this.moveSpatial(dir, 0);
  }

  /** ↑/↓ — spatial move to the nearest item vertically (#2 gamepad flow). */
  moveVertical(dir: number): void {
    this.moveSpatial(0, dir);
  }

  /**
   * Directional navigation (docs/12 #2): D-pad moves spatially with
   * wrap-around at the edges — the math lives in shop-nav.ts.
   */
  private moveSpatial(dx: number, dy: number): void {
    const next = spatialMove(this.positions, this.selected, dx, dy);
    if (next < 0 || next === this.selected) return;
    this.selected = next;
    this.hangar.armed = null;
    sfx.uiMove();
    this.rebuild();
  }

  activateSelected(): void {
    const item = this.items[this.selected];
    if (item) this.activate(item);
  }

  /** Y/I — toggles the weapon info panel for the focused hangar item. */
  toggleInfo(): void {
    this.infoOpen = !this.infoOpen;
    this.rebuild();
  }

  /** X / Ⓧ — scraps (press twice) a bag weapon / the crate, locks a card. */
  trashSelected(): void {
    const item = this.items[this.selected];
    if (item?.kind === 'card') this.callbacks.toggleLock(item.index);
    else if (!this.scrap(item)) sfx.uiDeny();
  }

  /** ✕ badge / crate scrap button (touch + mouse). */
  private scrapItem(weaponId: string): void {
    const item: FocusItem = { kind: 'weapon', weaponId };
    this.select(item);
    this.scrap(item);
  }

  /** Arm / scrap; a real scrap refreshes inside — the hand moves to a neighbour. */
  private scrap(item: FocusItem | undefined): boolean {
    const s = this.state;
    if (!s || item?.kind !== 'weapon') return false;
    this.wanted = this.hangar.afterScrap(s, item.weaponId);
    const handled = this.hangar.scrap(s, item);
    this.wanted = null; // consumed by the refresh, or nothing happened
    if (handled) this.rebuild();
    return handled;
  }

  private activate(item: FocusItem): void {
    const s = this.state;
    switch (item.kind) {
      case 'card':
        this.callbacks.buy(item.index); // instant buy (user call, N1 reverted)
        break;
      case 'socket':
      case 'weapon':
      case 'bagBuy': {
        if (!s) return;
        // Equips/buys refresh synchronously inside; pins and denials do not —
        // either way the hand moves on and the bay redraws once more
        const next = this.hangar.activate(s, item);
        if (next) this.select(next);
        this.rebuild();
        break;
      }
      case 'chip':
        // Inspect-only: focusing it shows the tooltip (touch taps land here)
        this.rebuild();
        break;
      case 'reroll':
        this.callbacks.reroll();
        break;
      case 'heal':
        this.callbacks.heal();
        break;
      case 'buyArmor':
        // ONE armor action: a stored plate first (free), else the yellow buy
        if (s && s.armorItems > 0 && !s.armorFull) this.callbacks.useArmor();
        else this.callbacks.buyArmor();
        break;
      case 'continue':
        this.callbacks.continueRun();
        break;
      case 'tab':
        // Compact page switch — the hand stays on the tab
        this.page = item.page;
        this.wanted = item;
        if (s) this.refresh(s);
        sfx.uiMove();
        break;
    }
  }

  hide(): void {
    this.open = false;
    this.view.visible = false;
  }
}
